import {currentFt3dFrame, postFt3dPresentation} from './scene-frame';
import {isCastEffectRow} from '../janus-engine/presentation/cast-commit';
import { rollVisibility } from '../janus-engine/rolls/visibility';
// Bridge an authorized canonical damage-roll row into the same-origin FT3D scene. The receiver
// resolves the target against perceived creature placements, so an unseen token never gains a
// world-space marker. Values also stay behind the combat log's private-roll predicate here.
import { attackImpactAt, presentedAttackImpact, THROW_FLIGHT_MS } from '../shared/attack-impact-clock';
import { rollPresentationTiming, SPELL_RELEASE_MS } from '../shared/spell-cast-timing';
import type { ProjectilePool } from '../shared/projectile-targeting';
import { RollWaterfallGroups } from './roll-waterfall';

type RollCatalogEntry = { item_id?: string; hex_color_code?: string };

type DamageCatalogEntry = {
    item_id?: string;
    item_name?: string;
    variable_name?: string;
    hex_color_code?: string;
};

type DamageWaterfallComponent = {
    damageTypeId: string;
    combinedDamageTypeIds?: string[];
    rolled: number;
    applied: number | null;
    reason: string;
};

type TriggerRuneItem = {
    items?: Record<string, unknown>;
    item_layer_types?: Record<string, unknown>;
    computed?: Record<string, unknown>;
    catalog?: Record<string, unknown>;
};

type DamageRollMeta = {
    damage_type_id?: unknown;
    ft_gate_passed?: unknown;
    kept?: unknown;
    kind?: unknown;
    reroll?: unknown;
};

type FloatingCombatOutcome = 'critical-hit' | 'glancing-blow';

type ContestAnchor = {
    rollBy: string;
    result: number;
};

const recentDamageRolls = new Set<string>();
const recentDamageRollOrder: string[] = [];
const animatedSourceActions = new Set<string>();
const contestAnchors = new Map<string, ContestAnchor>();
const contestAnchorOrder: string[] = [];
const attackCues = new Map<string, 'attack' | 'throw' | 'cast'>();
// This spell requires an equipped melee weapon and one cleave across its recipients.
// Other spell-melee attacks may conjure their own blade or use a casting gesture.
const BLAZING_CLEAVE_ID = 'E57464C5-58A9-4E23-3D6A-A4F9BB882425';

function usesEquippedWeaponSwing(roll: Record<string, unknown>): boolean {
    return String(roll.roll_item_id || '').trim().toUpperCase() === BLAZING_CLEAVE_ID;
}

function attackCueKey(roll: Record<string, unknown>): string {
    return [roll.roll_id, roll.roll_by || roll.roll_creature_id,
        roll.roll_to || roll.roll_target_creature_id, roll.roll_item_id]
        .map(value => String(value || '').trim().toUpperCase()).join(':');
}

function rememberAttackCue(roll: Record<string, unknown>): void {
    const catalog = (window as unknown as {
        ROLLTYPE_CATALOG?: Record<string, RollCatalogEntry>;
    }).ROLLTYPE_CATALOG;
    if (!roll.roll_id) return;
    for (const name of ['weapon_melee_attack_roll', 'weapon_ranged_attack_roll',
        'spell_melee_attack_roll', 'spell_ranged_attack_roll']) {
        if (!catalog?.[name]?.item_id || catalog[name].item_id !== roll.roll_type_1) continue;
        attackCues.set(attackCueKey(roll), roll.roll_type_2 === '1A3033B2-1FA4-485A-9D0A-1CD0BC92C961' ? 'throw' : name.startsWith('spell_') && !(
            name === 'spell_melee_attack_roll' && usesEquippedWeaponSwing(roll)) ? 'cast' : 'attack');
        if (attackCues.size > 200) attackCues.delete(attackCues.keys().next().value!);
        return;
    }
}

/**
 * The applying commit marks a visual-less cast, once per action. Later damage has no cast clock.
 * Its release puts the gesture's peak where the mixer puts the cast sound.
 */
function postResolvedSpellCast(roll: Record<string, unknown>): void {
    // A still ability (Short Rest; motion 'none' = STILL_ABILITY_MOTION in ft3d-avatar-ability-motion.ts) never gestures.
    if (roll.roll_vfx || roll.roll_motion === 'none') return;
    const marked = (roll.roll_cast_clock as { gesture?: unknown } | undefined)?.gesture === 'cast';
    if (!marked) return;
    const timing = rollPresentationTiming(roll);
    const rollId = String(roll.roll_id || '').trim();
    const sourceEntityId = String(roll.roll_by || '').trim().toUpperCase();
    const targetEntityId = String(roll.roll_to || '').trim().toUpperCase();
    if (!rollId || !sourceEntityId) return;
    const key = `${rollId}:${sourceEntityId}`;
    if (animatedSourceActions.has(key)) return;
    animatedSourceActions.add(key);
    if (animatedSourceActions.size > 200) animatedSourceActions.delete(animatedSourceActions.values().next().value!);
    postFt3dPresentation({ type: 'ft3d-campaign-animation-cue-v1', payload: {
        schema: 'ft.ft3d.animation-cue.v1', rollId, rollI: Number(roll.roll_i) || null,
        sourceEntityId, targetEntityId, kind: 'cast', sourceKind: 'cast',
        ...(typeof roll.roll_motion === 'string' ? { motion: roll.roll_motion } : {}),
        ...(timing ? { releaseAt: timing.gestureAt + SPELL_RELEASE_MS } : {}),
    } });
}
const FLOATING_DAMAGE_KEY = 'ft3d.hud.floatingDamage';
const FLOATING_DAMAGE_SIZE_KEY = 'ft3d.hud.floatingDamageSize';
const FLOATING_DAMAGE_MOTION_KEY = 'ft3d.hud.floatingDamageMotion';
export type Ft3dFloatingDamageSize = 'small' | 'normal' | 'large';
/**
 * `always` animates the waterfall even when the OS asks for reduced motion; `system` honours the OS
 * and shows the numbers already separated with a plain fade. Defaults to `always`: floating
 * feedback is itself an explicit opt-in above, and on Windows the reduce-motion flag is set by the
 * unrelated "Animation effects" switch, which silently hid the bloom from the owner (2026-08-25).
 */
export type Ft3dFloatingDamageMotion = 'always' | 'system';

export function isFt3dFloatingDamageEnabled(): boolean {
    try {
        // Preserve the shipped behavior for viewers who have never made a choice.
        return window.localStorage.getItem(FLOATING_DAMAGE_KEY) !== 'off';
    } catch {
        return true;
    }
}

export function setFt3dFloatingDamageEnabled(enabled: boolean): void {
    try { window.localStorage.setItem(FLOATING_DAMAGE_KEY, enabled ? 'on' : 'off'); } catch { /* viewer-local */ }
}

export function getFt3dFloatingDamageSize(): Ft3dFloatingDamageSize {
    try {
        const stored = window.localStorage.getItem(FLOATING_DAMAGE_SIZE_KEY);
        return stored === 'small' || stored === 'large' ? stored : 'normal';
    } catch {
        return 'normal';
    }
}

export function setFt3dFloatingDamageSize(size: Ft3dFloatingDamageSize): void {
    try { window.localStorage.setItem(FLOATING_DAMAGE_SIZE_KEY, size); } catch { /* viewer-local */ }
}

export function getFt3dFloatingDamageMotion(): Ft3dFloatingDamageMotion {
    try {
        return window.localStorage.getItem(FLOATING_DAMAGE_MOTION_KEY) === 'system' ? 'system' : 'always';
    } catch {
        return 'always';
    }
}

export function setFt3dFloatingDamageMotion(motion: Ft3dFloatingDamageMotion): void {
    try { window.localStorage.setItem(FLOATING_DAMAGE_MOTION_KEY, motion); } catch { /* viewer-local */ }
}

const DAMAGE_TRANSFER_ROLL = 'transfer_damage_percentage_to_caster_of_status_effect';

function isCanonicalDamageRoll(rollTypeId: string): boolean {
    if (rollTypeId === DAMAGE_TRANSFER_ROLL) return true;
    const catalog = (window as unknown as {
        ROLLTYPE_CATALOG?: Record<string, RollCatalogEntry>;
    }).ROLLTYPE_CATALOG;
    if (!catalog) return false;
    return [catalog.damage_roll?.item_id, catalog.damage_over_time_roll?.item_id]
        .some(id => String(id || '') === rollTypeId);
}

function catalogEntryForDamageType(id: string): DamageCatalogEntry | null {
    const catalog = (window as unknown as {
        DAMAGETYPE_CATALOG?: Record<string, DamageCatalogEntry>;
    }).DAMAGETYPE_CATALOG;
    if (!catalog || id === '') return null;
    const normalized = id.toUpperCase();
    return catalog[id]
        ?? Object.entries(catalog).find(([key, entry]) => (
            key.toUpperCase() === normalized || String(entry?.item_id || '').toUpperCase() === normalized
        ))?.[1]
        ?? null;
}

function damageTypeIconKey(entry: DamageCatalogEntry | null, fallbackName: unknown): string {
    if (!entry) return 'damage_roll';
    const catalogKey = String(entry?.variable_name || '').trim().toLowerCase();
    if (/^[a-z][a-z0-9_-]{0,47}$/.test(catalogKey)) return catalogKey;
    const nameKey = String(fallbackName || '').trim().toLowerCase()
        .replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
    return /^[a-z][a-z0-9_-]{0,47}$/.test(nameKey) ? nameKey : 'damage_roll';
}

/**
 * Show exact applied amounts per type when available. Untyped mitigation or transfer can make the
 * split unknowable; those rows use one final total with a generic damage icon. Never redistribute
 * that total or substitute the pre-mitigation type amounts.
 */
function damageWaterfallComponents(roll: Record<string, unknown>): DamageWaterfallComponent[] {
    const rawFeedback = recordOf(roll.roll_feedback);
    const rawComponents = rawFeedback?.damage_types;
    if (Array.isArray(rawComponents)) {
        const components: DamageWaterfallComponent[] = [];
        for (const rawComponent of rawComponents) {
            const component = recordOf(rawComponent);
            const damageTypeId = String(component?.damage_type_id || '').trim();
            const rolled = Number(component?.rolled);
            const rawApplied = component?.applied;
            const applied = rawApplied === null || rawApplied === undefined ? null : Number(rawApplied);
            if (!damageTypeId || !Number.isFinite(rolled) || rolled < 0
                || (applied !== null && (!Number.isFinite(applied) || applied < 0))) continue;
            components.push({
                damageTypeId,
                rolled,
                applied,
                reason: String(component?.reason || ''),
            });
        }
        if (rawComponents.length > 0) {
            const final = roll.roll_result === null || roll.roll_result === undefined || roll.roll_result === ''
                ? NaN : Number(roll.roll_result);
            const total = typeof rawFeedback?.applied === 'number' && Number.isFinite(rawFeedback.applied)
                && rawFeedback.applied >= 0 ? rawFeedback.applied : final;
            const exact = components.length === rawComponents.length && components.every(component => component.applied !== null)
                && (!Number.isFinite(total) || components.reduce((sum, component) => sum + component.applied!, 0) === total);
            if (exact) return components;
            if (!Number.isFinite(total) || total < 0) return [];
            const typeIds = [...new Set(components.map(component => component.damageTypeId.toUpperCase()))];
            const combined = typeIds.length !== 1 || components.length !== rawComponents.length;
            return [{
                damageTypeId: combined ? '' : components[0]!.damageTypeId,
                combinedDamageTypeIds: combined ? typeIds : undefined,
                rolled: total,
                applied: total,
                reason: String(rawFeedback?.reason || ''),
            }];
        }
    }

    if (roll.roll_result === null || roll.roll_result === undefined || roll.roll_result === '') return [];
    const final = Number(roll.roll_result);
    if (!Number.isFinite(final) || final < 0) return [];
    const rolled = typeof rawFeedback?.rolled === 'number' ? rawFeedback.rolled : NaN;
    const applied = typeof rawFeedback?.applied === 'number' ? rawFeedback.applied : NaN;
    const known = Number.isFinite(rolled) && rolled >= 0 && Number.isFinite(applied) && applied >= 0;
    return [{
        damageTypeId: String(roll.roll_type_2 || '').trim(),
        rolled: known ? rolled : final,
        applied: known ? applied : null,
        reason: known ? String(rawFeedback?.reason || '') : '',
    }];
}

type ResourceRollFeedback = { name: string; color: string; kind: 'roll-gain' | 'roll-loss' | 'roll-value' };
const RESOURCE_ROLL_FEEDBACK: Record<string, ResourceRollFeedback> = {
    healing_roll: { name: 'Healing', color: '#7CFF92', kind: 'roll-gain' },
    regeneration_roll: { name: 'Regeneration', color: '#7CFF92', kind: 'roll-gain' },
    temporary_vitality_roll: { name: 'Temp vitality', color: '#8DDCFF', kind: 'roll-value' },
    temporary_vitality_regeneration_roll: { name: 'Temp vitality regen', color: '#8DDCFF', kind: 'roll-value' },
    temporary_max_vitality_roll: { name: 'Temp max vitality', color: '#BDA4FF', kind: 'roll-value' },
    increase_mana_roll: { name: 'Mana', color: '#8DBAFF', kind: 'roll-gain' },
    reduce_mana_roll: { name: 'Mana', color: '#8DBAFF', kind: 'roll-loss' },
    increase_luck_roll: { name: 'Luck', color: '#FFD166', kind: 'roll-gain' },
    reduce_luck_roll: { name: 'Luck', color: '#FFD166', kind: 'roll-loss' },
};

function resourceRollFeedback(rollTypeId: string): (ResourceRollFeedback & { icon: string }) | null {
    if (rollTypeId === 'damage_heals_caster') return { name: 'Healing', color: '#7CFF92', kind: 'roll-gain', icon: 'healing_roll' };
    const catalog = (window as unknown as {
        ROLLTYPE_CATALOG?: Record<string, RollCatalogEntry>;
    }).ROLLTYPE_CATALOG;
    if (!rollTypeId || !catalog) return null;
    const key = Object.keys(RESOURCE_ROLL_FEEDBACK).find(key => catalog[key]?.item_id === rollTypeId);
    const feedback = key ? RESOURCE_ROLL_FEEDBACK[key] : null;
    return key && feedback ? { ...feedback, icon: key } : null;
}

function isCanonicalContestingRoll(rollTypeId: string): boolean {
    const catalog = (window as unknown as {
        ROLLTYPE_CATALOG?: Record<string, RollCatalogEntry>;
    }).ROLLTYPE_CATALOG;
    return String(catalog?.contesting_roll?.item_id || '') === rollTypeId;
}

function rollIsVisibleToViewer(roll: Record<string, unknown>): boolean {
    return rollVisibility(roll) === 'values';
}

function markDamageRollSeen(key: string): boolean {
    if (recentDamageRolls.has(key)) return false;
    recentDamageRolls.add(key);
    recentDamageRollOrder.push(key);
    if (recentDamageRollOrder.length > 200) {
        const oldest = recentDamageRollOrder.shift();
        if (oldest) recentDamageRolls.delete(oldest);
    }
    return true;
}

function parsedRollsFull(raw: unknown): unknown[] {
    if (Array.isArray(raw)) return raw;
    if (typeof raw !== 'string' || raw.trim() === '') return [];
    try {
        const parsed = JSON.parse(raw) as unknown;
        return Array.isArray(parsed) ? parsed : [];
    } catch {
        return [];
    }
}

function recordOf(value: unknown): Record<string, unknown> | null {
    return value !== null && typeof value === 'object' && !Array.isArray(value)
        ? value as Record<string, unknown>
        : null;
}

/**
 * Resolve the triggered ability from the viewer-authorized encounter cache. The waterfall receives
 * this small rune input instead of re-querying or shipping the entire encounter into the iframe.
 */
function triggerRuneItem(effectId: string): TriggerRuneItem | null {
    const host = window as unknown as {
        FateTide?: {
            encounterArr?: unknown;
            encounterCache?: Map<string, unknown>;
        };
        ft3dEncounterArrCache?: { encounterId?: unknown; arr?: unknown };
    };
    const surface = currentFt3dFrame()?.closest<HTMLElement>('[data-ft3d-campaign-encounter-view]');
    const encounterId = String(surface?.dataset.encounterId || '').trim();
    const candidates: unknown[] = [];
    const local = host.ft3dEncounterArrCache;
    if (local?.arr && (!encounterId || String(local.encounterId || '').toUpperCase() === encounterId.toUpperCase())) {
        candidates.push(local.arr);
    }
    const cache = host.FateTide?.encounterCache;
    if (cache instanceof Map && encounterId) {
        const exact = cache.get(encounterId) ?? cache.get(encounterId.toUpperCase());
        if (exact) candidates.push(exact);
        if (!exact) {
            for (const [key, value] of cache) {
                if (String(key).toUpperCase() === encounterId.toUpperCase()) {
                    candidates.push(value);
                    break;
                }
            }
        }
    }
    if (host.FateTide?.encounterArr) candidates.push(host.FateTide.encounterArr);

    for (const candidate of candidates) {
        const catalog = recordOf(recordOf(candidate)?.catalog);
        if (!catalog) continue;
        let item = recordOf(catalog[effectId]) ?? recordOf(catalog[effectId.toLowerCase()]);
        if (!item) {
            item = Object.values(catalog).map(recordOf).find(entry => (
                String(recordOf(entry?.items)?.item_id || '').trim().toUpperCase() === effectId
            )) ?? null;
        }
        const items = recordOf(item?.items);
        if (!item || !items) continue;
        return {
            items,
            item_layer_types: recordOf(item.item_layer_types) ?? {},
            computed: recordOf(item.computed) ?? {},
            catalog: recordOf(item.catalog) ?? {},
        };
    }
    return null;
}

function floatingCombatOutcomes(roll: Record<string, unknown>): FloatingCombatOutcome[] {
    let critical = false;
    let glancing = false;
    for (const rawTuple of parsedRollsFull(roll.rolls_full)) {
        if (!Array.isArray(rawTuple)) continue;
        const label = String(rawTuple[1] ?? '').trim().toLowerCase();
        const rawMeta = rawTuple[2];
        const meta = rawMeta && typeof rawMeta === 'object' && !Array.isArray(rawMeta)
            ? rawMeta as DamageRollMeta
            : {};
        if (String(meta.kind || '').toLowerCase() === 'critical') critical = true;
        if (label === 'half damage') glancing = true;
    }
    const outcomes: FloatingCombatOutcome[] = [];
    if (critical) outcomes.push('critical-hit');
    if (glancing) outcomes.push('glancing-blow');
    return outcomes;
}

/** roll_id → the impact (epoch ms) its swing was presented on at send (#8c); its floats land there too. */
const presentedRollImpacts = new Map<string, number>();
function postCombatFloat(
    payload: Record<string, unknown>,
): void {
    const impactAt = presentedRollImpacts.get(String(payload.rollId ?? '').split(':')[0] ?? '');
    if (impactAt !== undefined) payload = { ...payload, impactAt };
    postFt3dPresentation({
        type: 'ft3d-campaign-combat-float-v1',
        payload: {
            schema: 'ft.ft3d.combat-float.v1',
            size: getFt3dFloatingDamageSize(),
            motion: getFt3dFloatingDamageMotion(),
            componentIndex: 0,
            componentCount: 1,
            ...payload,
        },
    });
}

/**
 * Animation cue for rigged FT3D avatars (animation lane slice 6): the actor swings or casts, the
 * target is hit or dodges, from the same authorized roll rows the floats read. One cue per roll.
 */
function postAnimationCue(roll: Record<string, unknown>, kind: 'hit' | 'miss', targetEntityId: string): void {
    // A damage row alone does not establish a new attack. Match its action, actors and
    // item to an authorized attack roll so status ticks and effect riders stay quiet.
    // Granted projectile actions include automatic hits and contests. Only the applying
    // commit owns their source gesture; subsequent damage still gets its target reaction.
    const projectile = (window as unknown as { ftGetProjectilePool?: (casterId: string, abilityId: string) => ProjectilePool | null }).ftGetProjectilePool?.(
        String(roll.roll_by || roll.roll_creature_id || ''), String(roll.roll_item_id || ''),
    );
    const sourceKind = isCastEffectRow(roll) ? 'effect' : attackCues.get(attackCueKey(roll))
        ?? (kind === 'miss' ? (isSpellRoll(roll) && !usesEquippedWeaponSwing(roll) ? 'cast' : 'attack') : projectile ? 'cast' : undefined);
    if (!sourceKind) return;
    const catalog = (window as unknown as {
        ROLLTYPE_CATALOG?: Record<string, RollCatalogEntry>;
    }).ROLLTYPE_CATALOG;
    if (kind === 'hit' && roll.roll_type_1 !== catalog?.damage_roll?.item_id) return;
    const rollId = String(roll.roll_id || roll.roll_i || '').trim();
    // Cast-sync #8c: attack-truth already presented this swing at SEND (and the target's reaction on its impact).
    // A second cue here would swing again; this roll's floats keep the same impact instead.
    const presented = (sourceKind === 'attack' || sourceKind === 'throw') && rollId
        ? presentedAttackImpact(rollId, String(roll.roll_by || roll.roll_creature_id || ''), targetEntityId) : null;
    if (presented !== null && rollId) {
        presentedRollImpacts.set(rollId, presented);
        if (presentedRollImpacts.size > 200) presentedRollImpacts.delete(presentedRollImpacts.keys().next().value!);
        return;
    }
    const sourceEntityId = String(roll.roll_by || roll.roll_creature_id || '').trim().toUpperCase();
    if (!rollId || !markDamageRollSeen(`cue:${rollId}:${kind}:${targetEntityId}`)) return;
    // Damage types and AOE victims share the action's roll_id. Each victim needs its own
    // reaction, but must not restart the caster's motion when its result arrives.
    const sourceKey = `${rollId}:${sourceEntityId}`;
    const publishedColor=(roll.roll_vfx as {recipe?:{color?:unknown}}|undefined)?.recipe?.color;
    const animateSource = sourceKind !== 'effect' && sourceEntityId !== '' && !animatedSourceActions.has(sourceKey);
    // A roll-row swing (attack abilities, legacy-lane weapon attacks) gets the shared attack impact clock too: the
    // scene retimes the swing onto it and the mixer lands these rows' hit/whiff sound there (#8c no-visual extension).
    const impactAt = animateSource && (sourceKind === 'attack' || sourceKind === 'throw')
        ? attackImpactAt(rollId, sourceEntityId, Date.now(), sourceKind === 'throw' ? THROW_FLIGHT_MS : 0) : undefined;
    if (animateSource) {
        animatedSourceActions.add(sourceKey);
        if (animatedSourceActions.size > 200) animatedSourceActions.delete(animatedSourceActions.values().next().value!);
    }
    postFt3dPresentation({
        type: 'ft3d-campaign-animation-cue-v1',
        // A server-authored magic cue drives its own context-aware gesture in the scene.
        // Still mark this action so later damage riders cannot restart a generic cast.
        payload: { schema: 'ft.ft3d.animation-cue.v1', rollId, rollI: Number(roll.roll_i) || null, itemId: String(roll.roll_item_id ?? ''),
            sourceEntityId: animateSource && !(sourceKind === 'cast' && roll.roll_vfx) ? sourceEntityId : '', targetEntityId, kind, sourceKind,
            ...(typeof roll.roll_motion === 'string' ? { motion: roll.roll_motion } : {}),
            ...(impactAt !== undefined ? { impactAt } : {}),
            ...(sourceKind === 'throw' && String(roll.roll_item_id ?? '').toUpperCase() !== 'EA8FB8F0-8FAA-4AC0-B939-F038057B50DA'
                ? { throwFlight: { inventoryId: String(roll.roll_item_id ?? ''), arcing: false }, releaseAt: impactAt === undefined ? undefined : impactAt - THROW_FLIGHT_MS } : {}),
            ...(typeof publishedColor==='string' && /^#[\da-f]{6}$/i.test(publishedColor) ? {trailColor:publishedColor}:{}) },
    });
}
function isSpellRoll(roll: Record<string, unknown>): boolean {
    return /spell/i.test(String(roll.roll_type_1 || ''));
}
function projectAttackMiss(roll: Record<string, unknown>): boolean {
    const kind = String(roll.roll_type_1 || '');
    if (kind !== 'weapon_attack_missed' && kind !== 'spell_attack_missed') return false;
    const targetEntityId = String(roll.roll_to || roll.roll_target_creature_id || '').trim().toUpperCase();
    const rollId = String(roll.roll_id || roll.roll_i || '').trim();
    if (targetEntityId && rollId) postAnimationCue(roll, 'miss', targetEntityId);
    if (!targetEntityId || !rollId || !markDamageRollSeen(`outcome:miss:${rollId}:${targetEntityId}`)) return true;
    postCombatFloat({
        rollI: Number(roll.roll_i) || null,
        rollId: `${rollId}:miss:${targetEntityId}`,
        targetEntityId,
        label: 'Miss',
        kind: 'miss',
        color: '#B8C1D1',
    });
    return true;
}

function projectTriggeredEffect(roll: Record<string, unknown>): void {
    const effectId = String(roll.triggered_effect_item_id || '').trim().toUpperCase();
    const effectName = String(roll.triggered_effect_item_name || '').trim().slice(0, 52);
    const effectType = String(roll.triggered_effect_item_type || '').trim().toLowerCase().slice(0, 48);
    const casterEntityId = String(roll.roll_by || roll.roll_creature_id || '').trim().toUpperCase();
    const rollId = String(roll.roll_id || roll.roll_i || '').trim();
    if (!effectId || !effectName || !casterEntityId || !rollId) return;
    if (!markDamageRollSeen(`triggered:${rollId}:${effectId}:${casterEntityId}`)) return;
    const runeItem = triggerRuneItem(effectId);
    postCombatFloat({
        rollI: Number(roll.roll_i) || null,
        rollId: `${rollId}:triggered:${effectId}:${casterEntityId}`,
        targetEntityId: casterEntityId,
        label: effectName,
        kind: 'triggered-effect',
        color: effectType === 'spell' ? '#8DDCFF' : '#FFD166',
        runeItem,
        effectItemId: effectId,
        effectItemType: effectType,
    });
}

function projectDamageOutcomes(roll: Record<string, unknown>, targetEntityId: string): void {
    const rollId = String(roll.roll_id || roll.roll_i || '').trim();
    if (!rollId) return;
    const outcomes = floatingCombatOutcomes(roll);
    outcomes.forEach((outcome, componentIndex) => {
        if (!markDamageRollSeen(`outcome:${outcome}:${rollId}:${targetEntityId}`)) return;
        const critical = outcome === 'critical-hit';
        postCombatFloat({
            rollI: Number(roll.roll_i) || null,
            rollId: `${rollId}:${outcome}:${targetEntityId}`,
            targetEntityId,
            label: critical ? 'Critical Hit!' : 'Glancing Blow',
            kind: outcome,
            color: critical ? '#FFB347' : '#D6C7FF',
            componentIndex,
            componentCount: outcomes.length,
        });
    });
}

function rememberContestAnchor(rollId: string, anchor: ContestAnchor): void {
    if (!contestAnchors.has(rollId)) contestAnchorOrder.push(rollId);
    contestAnchors.set(rollId, anchor);
    if (contestAnchorOrder.length > 100) {
        const oldest = contestAnchorOrder.shift();
        if (oldest) contestAnchors.delete(oldest);
    }
}

// Stealth v2 (owner 2026-08-27): the acting creature's identity header ([data-ft3d-actor-head], the
// FT3D "profile") gets a timed banner when THAT creature is the spotter ("Spotted <name>!") or the
// one caught ("You were spotted!" — the watcher's name is only shown to the GM, who perceives all).
const STEALTH_HEADER_MS = 7000;
let stealthHeaderTimer: number | undefined;
function showStealthSpottedHeader(
    spotterId: string,
    spottedId: string,
    spotterName: string,
    spottedName: string,
    spotterKnown = true,
): void {
    const getActive = (window as unknown as { ftGetActiveCasterId?: () => unknown }).ftGetActiveCasterId;
    const activeId = typeof getActive === 'function' ? String(getActive() ?? '').trim().toUpperCase() : '';
    if (!activeId) return;
    const head = document.querySelector<HTMLElement>('[data-ft3d-actor-head]');
    if (!head) return;
    const isGm = !!(window as unknown as { FateTide?: { user?: { isGM?: boolean } } }).FateTide?.user?.isGM;
    let text = '';
    let tone: 'spotter' | 'spotted' = 'spotter';
    if (!spotterKnown && activeId === spottedId) {
        text = 'You were spotted!';
        tone = 'spotted';
    } else if (activeId === spotterId) {
        text = `Spotted ${spottedName}!`;
    } else if (activeId === spottedId) {
        text = isGm ? `Seen by ${spotterName}!` : 'You were spotted!';
        tone = 'spotted';
    } else {
        return;
    }
    let badge = head.querySelector<HTMLElement>('[data-ft3d-actor-head-spotted]');
    if (!badge) {
        badge = document.createElement('span');
        badge.className = 'ft3d-actor-head__spotted';
        badge.setAttribute('data-ft3d-actor-head-spotted', '');
        badge.setAttribute('role', 'status');
        head.appendChild(badge);
    }
    badge.textContent = text;
    badge.dataset.tone = tone;
    badge.hidden = false;
    badge.classList.remove('is-live');
    // Restart the CSS animation even when a second sighting lands inside the window.
    void badge.offsetWidth;
    badge.classList.add('is-live');
    if (stealthHeaderTimer !== undefined) window.clearTimeout(stealthHeaderTimer);
    stealthHeaderTimer = window.setTimeout(() => {
        badge.hidden = true;
        badge.classList.remove('is-live');
        stealthHeaderTimer = undefined;
    }, STEALTH_HEADER_MS);
}

function projectStealthSpotted(roll: Record<string, unknown>): void {
    const rollId = String(roll.roll_id || '').trim();
    // Public reveal row: roll_by = spotter, roll_to = spotted (owner 2026-08-28 — awareness is shared).
    // The spotter's NAME is only floated when this viewer perceives the spotter (FT3D swaps in
    // fallbackLabel otherwise); GMs skip this public hint and use the private prompt row instead.
    const spotterId = String(roll.roll_by || roll.roll_creature_id || '').trim().toUpperCase();
    const spottedId = String(roll.roll_to || roll.roll_target_creature_id || '').trim().toUpperCase();
    if (!rollId || !spotterId || !spottedId) return;
    if (!markDamageRollSeen(`stealth:${rollId}:${spotterId}:${spottedId}`)) return;
    const clip = (value: unknown, fallback: string): string => {
        const text = String(value ?? '').trim();
        return (text || fallback).slice(0, 24);
    };
    // Live rows carry the denormalized roll_by_item_name / roll_to_item_name columns; the
    // roll_creature_name / roll_target_creature_name aliases exist only on some re-rendered rows.
    const spotterName = clip(roll.roll_by_item_name ?? roll.roll_creature_name, 'Someone');
    const spottedName = clip(roll.roll_to_item_name ?? roll.roll_target_creature_name, 'someone');
    // GAVE THEMSELVES AWAY (owner 2026-09-15): a reveal row whose two sides are the SAME creature is a
    // self-reveal — speaking in character ended the stance (ft_chat_break_hiding). Nobody spotted them,
    // so there is no spotter to name and no "who saw whom" header: one float on the speaker.
    const selfRevealed = spotterId === spottedId;
    const floats: Array<{ targetEntityId: string; label: string; fallbackLabel: string; color: string }> = selfRevealed
        ? [{ targetEntityId: spottedId, label: 'Revealed!', fallbackLabel: 'Revealed!', color: '#FF6B6B' }]
        : [
            { targetEntityId: spotterId, label: `Spotted ${spottedName}!`, fallbackLabel: 'Spotted!', color: '#FFD166' },
            { targetEntityId: spottedId, label: `Seen by ${spotterName}!`, fallbackLabel: 'Spotted!', color: '#FF6B6B' },
        ];
    if (!selfRevealed) showStealthSpottedHeader(spotterId, spottedId, spotterName, spottedName, true);
    floats.forEach((float) => {
        postFt3dPresentation({
            type: 'ft3d-campaign-combat-float-v1',
            payload: {
                schema: 'ft.ft3d.combat-float.v1',
                rollI: Number(roll.roll_i) || null,
                rollId: `${rollId}:${float.targetEntityId}`,
                targetEntityId: float.targetEntityId,
                spotterEntityId: spotterId,
                label: float.label,
                fallbackLabel: float.fallbackLabel,
                kind: 'stealth-spotted',
                color: float.color,
                size: 'large',
                motion: getFt3dFloatingDamageMotion(),
                componentIndex: 0,
                componentCount: 1,
            },
        });
    });
}

function projectContestOutcome(
    roll: Record<string, unknown>,
): void {
    const rollId = String(roll.roll_id || '').trim();
    const rollBy = String(roll.roll_by || roll.roll_creature_id || '').trim().toUpperCase();
    const rollTo = String(roll.roll_to || '').trim().toUpperCase();
    const result = Number(roll.roll_result);
    if (!rollId || !rollBy || !Number.isFinite(result)) return;

    const anchor = contestAnchors.get(rollId);
    // The initiating contest/DC row has no opponent. Retain it so every later defender in the
    // shared roll can be compared without waiting for a grouped-log rerender.
    if (!anchor || rollTo === '') {
        rememberContestAnchor(rollId, { rollBy, result });
        return;
    }
    if (rollBy === anchor.rollBy || (rollTo !== '' && rollTo !== anchor.rollBy)) return;
    // FateTide contests award ties to the initiator. "Beat" therefore means strictly greater.
    if (result <= anchor.result) return;

    postFt3dPresentation({
        type: 'ft3d-campaign-combat-float-v1',
        payload: {
            schema: 'ft.ft3d.combat-float.v1',
            rollI: Number(roll.roll_i) || null,
            rollId,
            targetEntityId: rollBy,
            label: 'Beat',
            kind: 'contest-success',
            color: '#7CFF92',
            size: getFt3dFloatingDamageSize(),
            motion: getFt3dFloatingDamageMotion(),
            componentIndex: 0,
            componentCount: 1,
        },
    });
}

export function mountFt3dDamageFeedback(): void {
    const groups = new RollWaterfallGroups();

    document.addEventListener('ft-roll-authorized', event => {
        const detail = (event as CustomEvent).detail as { roll?: unknown } | null;
        if (!detail?.roll || typeof detail.roll !== 'object' || Array.isArray(detail.roll)) return;
        const roll = detail.roll as Record<string, unknown>;
        if (!rollIsVisibleToViewer(roll)) return;
        // Stealth v2 (owner 2026-08-27): a hidden creature was spotted — float "Spotted <name>!" over
        // the spotter and "Seen by <name>!" over the creature that was caught. Not gated by the
        // floating-damage preference: being caught is a game beat, not a damage readout.
        if (String(roll.roll_type_1 || '') === 'target_creature_revealed') {
            projectStealthSpotted(roll);
            return;
        }
        postResolvedSpellCast(roll);
        if (!isFt3dFloatingDamageEnabled()) return;

        rememberAttackCue(roll);

        projectTriggeredEffect(roll);

        if (projectAttackMiss(roll)) return;

        const rollTypeId = String(roll.roll_type_1 || '');
        if (isCanonicalContestingRoll(rollTypeId)) {
            projectContestOutcome(roll);
            return;
        }
        const resource = resourceRollFeedback(rollTypeId);
        if (!isCanonicalDamageRoll(rollTypeId) && !resource) return;

        const targetEntityId = String(roll.roll_to || roll.roll_target_creature_id || '')
            .trim().toUpperCase();
        const amount = Math.max(0, Number(roll.roll_result || 0));
        if (!targetEntityId || !Number.isFinite(amount)) return;
        if (!resource) postAnimationCue(roll, 'hit', targetEntityId);

        if (!resource) projectDamageOutcomes(roll, targetEntityId);

        const rollKey = String(roll.roll_i || '') || `${String(roll.roll_id || '')}:${rollTypeId}:${targetEntityId}:${amount}`;
        if (!markDamageRollSeen(rollKey)) return;

        const catalog = (window as unknown as { ROLLTYPE_CATALOG?: Record<string, RollCatalogEntry> }).ROLLTYPE_CATALOG;
        if (resource) {
            const summary = groups.add(roll, resource.icon, targetEntityId);
            if (!summary) return;
            const catalogColor = String(catalog?.[resource.icon]?.hex_color_code || '');
            postCombatFloat({
                ...summary,
                rollI: Number(roll.roll_i) || null,
                targetEntityId,
                kind: 'roll-summary',
                damageTypeName: resource.name,
                damageTypeIcon: resource.icon,
                color: /^#[0-9a-f]{3,8}$/i.test(catalogColor) ? catalogColor : resource.color,
                label: String(summary.amount),
            });
            return;
        }

        // Transfer rows store the warding status in type_2, not a damage type.
        // Keep their authoritative recipient/amount without inventing an elemental type.
        const damageRoll = rollTypeId === DAMAGE_TRANSFER_ROLL
            ? { ...roll, roll_type_2: '', roll_type_2_name: 'Damage' } : roll;
        for (const component of damageWaterfallComponents(damageRoll)) {
            const hasAffinity = (kind: 'resistance' | 'immunity' | 'vulnerability'): boolean =>
                parsedRollsFull(roll.rolls_full).some(tuple => {
                    if (!Array.isArray(tuple)) return false;
                    const meta = recordOf(tuple[2]);
                    const typeId = String(meta?.damage_type_id || '').toUpperCase();
                    return meta?.kind === `spell_${kind}` || (meta?.kind === kind
                        && (component.combinedDamageTypeIds?.includes(typeId) ?? (typeId === component.damageTypeId.toUpperCase())));
                });
            const damageType = catalogEntryForDamageType(component.damageTypeId);
            const damageTypeName = String(component.combinedDamageTypeIds ? 'Damage' : damageType?.item_name || damageRoll.roll_type_2_name || 'Damage')
                .trim().slice(0, 40) || 'Damage';
            const componentRoll: Record<string, unknown> = {
                ...roll,
                roll_result: component.applied ?? component.rolled,
                roll_feedback: component.applied === null ? null : {
                    rolled: component.rolled,
                    applied: component.applied,
                    verb: 'taken',
                    reason: component.reason,
                    resisted: hasAffinity('resistance'),
                    immune: hasAffinity('immunity'),
                    vulnerable: hasAffinity('vulnerability'),
                },
            };
            const discriminator = component.damageTypeId.toUpperCase() || damageTypeName.toUpperCase();
            const summary = groups.add(componentRoll, 'damage_roll', targetEntityId, Date.now(), discriminator);
            if (!summary) continue;
            const rawColor = String(damageType?.hex_color_code || '');
            postCombatFloat({
                ...summary,
                rollI: Number(roll.roll_i) || null,
                targetEntityId,
                kind: 'roll-summary',
                damageTypeId: component.damageTypeId,
                damageTypeName,
                damageTypeIcon: damageTypeIconKey(damageType, damageTypeName),
                color: /^#[0-9a-f]{3,8}$/i.test(rawColor) ? rawColor : '#f06b6b',
                label: String(summary.amount),
            });
        }
    });
}
